import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import React, { useMemo, forwardRef, useImperativeHandle } from 'react';
import { makeStyles } from '@mui/styles';
import {
   Table,
   TableBody,
   TableCell,
   TableContainer,
   TableHead,
   TableRow,
   Paper,
   CircularProgress,
   Box,
   Typography,
   Tooltip,
   Button,
} from '@mui/material';
import {
   CheckCircle as CheckCircleIcon,
   Warning as WarningIcon,
   Error as ErrorIcon,
} from '@mui/icons-material';
import DataLoadingAndErrors from '../../fhg/components/DataLoadingAndErrors';

const useStyles = makeStyles(theme => ({
   container: {
      marginTop: theme.spacing(2),
   },
   table: {
      minWidth: 750,
   },
   tableHeader: {
      backgroundColor: theme.palette.grey[200],
      fontWeight: 'bold',
   },
   noData: {
      textAlign: 'center',
      padding: theme.spacing(3),
   },
}));

const GAUGE_CALIBRATIONS_QUERY = gql`
   query getGaugeCalibrations($operatorId: Int!) {
      gaugeCalibrations: gaugeCalibrations_ByOperatorId(operatorId: $operatorId) {
         id
         gaugeId
         operatorId
         calibrationDate
         expirationDate
         validityPeriod
         createdByUserId
         createdDateTime
         updatedByUserId
         updatedDateTime
         createdByUser {
            id
            name
            email
         }
         operator {
            id
            calibrationSettings {
               warningPeriod
            }
         }
      }
   }
`;


/**
 * Format date string/timestamp to readable date
 * Handles Unix timestamp (milliseconds), ISO date (YYYY-MM-DD), or ISO 8601 format
 */ 
const formatDate = (dateInput) => {
   if (!dateInput) return '-';
   try {
      let date;
      
      // Handle string timestamps (milliseconds as string) or numbers
      if (typeof dateInput === 'string' || typeof dateInput === 'number') {
         const timestamp = typeof dateInput === 'string' ? parseInt(dateInput, 10) : dateInput;
         
         // Check if it's a valid number
         if (isNaN(timestamp)) {
            // Try parsing as ISO date string if parseInt failed
            // Handle YYYY-MM-DD format explicitly to avoid timezone issues
            if (typeof dateInput === 'string' && dateInput.match(/^\d{4}-\d{2}-\d{2}$/)) {
               const [year, month, day] = dateInput.split('-').map(Number);
               date = new Date(year, month - 1, day);
            } else {
               // Try standard date parsing for ISO 8601 with time
               date = new Date(dateInput);
            } 
         } else {
            // Treat as Unix timestamp in milliseconds
            date = new Date(timestamp);
         }
      } else {
         return '-';
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) return '-';
      
      // Format as MM/DD/YYYY
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      
      return `${year}-${month}-${day}`;
   } catch (e) {
      return '-';
   }
};

/**
 * Determine calibration status based on expiration date and warning period
 * @returns {Object} { status: 'valid'|'warning'|'expired', icon: Component, color: string, label: string }
 */
const getCalibrationStatus = (expirationDate, warningPeriod = 14) => {
   if (!expirationDate) {
      return { status: 'unknown', icon: null, color: 'gray', label: 'Unknown' };
   }

   try {
      let expDate;
      if (typeof expirationDate === 'string' && expirationDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
         const [year, month, day] = expirationDate.split('-').map(Number);
         expDate = new Date(year, month - 1, day);
      } else {
         expDate = new Date(expirationDate);
      }

      if (isNaN(expDate.getTime())) {
         return { status: 'unknown', icon: null, color: 'gray', label: 'Unknown' };
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      expDate.setHours(0, 0, 0, 0);

      // Calculate warning date (warningPeriod days before expiration)
      const warningDate = new Date(expDate);
      warningDate.setDate(warningDate.getDate() - warningPeriod);

      if (today > expDate) {
         // Expired
         return { status: 'expired', icon: ErrorIcon, color: '#d32f2f', label: 'Expired' };
      } else if (today >= warningDate) {
         // Warning period
         return { status: 'warning', icon: WarningIcon, color: '#f57c00', label: 'Warning' };
      } else {
         // Valid
         return { status: 'valid', icon: CheckCircleIcon, color: '#388e3c', label: 'Valid' };
      }
   } catch (e) {
      return { status: 'unknown', icon: null, color: 'gray', label: 'Unknown' };
   }
};

export default forwardRef(function GaugeCalibrationTable({ operatorId }, ref) {
   const classes = useStyles();
   const { data, loading, error, refetch } = useQuery(GAUGE_CALIBRATIONS_QUERY, {
      variables: { operatorId },
      skip: !operatorId,
   });

   // Expose refetch function via ref
   useImperativeHandle(ref, () => ({
      refetch: () => refetch(),
   }));

   const calibrations = useMemo(() => {
      const allCalibrations = data?.gaugeCalibrations || [];
      
      // Group by gaugeId and keep only the latest (most recently created) per gauge
      const latestByGauge = {};
      
      allCalibrations.forEach(calibration => {
         const gaugeId = calibration.gaugeId;
         
         // If this gauge hasn't been seen or this record is newer, keep it
         if (!latestByGauge[gaugeId] || 
             new Date(calibration.createdDateTime) > new Date(latestByGauge[gaugeId].createdDateTime)) {
            latestByGauge[gaugeId] = calibration;
         }
      });
      
      // Convert back to array and sort by createdDateTime descending
      return Object.values(latestByGauge).sort((a, b) => 
         new Date(b.createdDateTime) - new Date(a.createdDateTime)
      );
   }, [data]);

   if (error) {
      return <DataLoadingAndErrors error={error} isLoading={false} />;
   }

   if (loading) {
      return (
         <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
         </Box>
      );
   }

   if (calibrations.length === 0) {
      return (
         <Box className={classes.noData}>
            <Typography variant="body1" color="textSecondary">
               No gauge calibrations recorded
            </Typography>
         </Box>
      );
   }

   return (
      <Box>
         <Box display="flex" justifyContent="flex-end" mb={2}>
            <Button 
               variant="contained" 
               color="primary" 
               size="small"
               onClick={() => refetch()}
            >
               Refresh
            </Button>
         </Box>
         <TableContainer component={Paper} className={classes.container}>
         <Table className={classes.table} size="small">
            <TableHead>
               <TableRow className={classes.tableHeader}>
                  <TableCell align="center"><strong>Status</strong></TableCell>
                  <TableCell><strong>Gauge ID</strong></TableCell>
                  <TableCell align="center"><strong>Calibration Date</strong></TableCell>
                  <TableCell align="center"><strong>Expiration Date</strong></TableCell>
                  <TableCell align="center"><strong>Validity Period (days)</strong></TableCell>
                  <TableCell align="center"><strong>Created By</strong></TableCell>
                  <TableCell align="center"><strong>Updated Date</strong></TableCell>
               </TableRow>
            </TableHead>
            <TableBody>
               {calibrations.map((calibration) => {
                  const warningPeriod = calibration.operator?.calibrationSettings?.warningPeriod || 14;
                  const status = getCalibrationStatus(calibration.expirationDate, warningPeriod);
                  const StatusIcon = status.icon;

                  return (
                     <TableRow key={calibration.id} hover>
                        <TableCell align="center">
                           <Tooltip title={status.label}>
                              {StatusIcon ? (
                                 <StatusIcon style={{ color: status.color, fontSize: '1.5rem' }} />
                              ) : (
                                 <span>-</span>
                              )}
                           </Tooltip>
                        </TableCell>
                        <TableCell>{calibration.gaugeId}</TableCell>
                        <TableCell align="center">{calibration.calibrationDate}</TableCell>
                        <TableCell align="center">{calibration.expirationDate}</TableCell>
                        <TableCell align="center">{calibration.validityPeriod}</TableCell>
                        <TableCell align="center">{calibration.createdByUser?.name || calibration.createdByUserId}</TableCell>
                        <TableCell align="center">{formatDate(calibration.updatedDateTime)}</TableCell>
                     </TableRow>
                  );
               })}
            </TableBody>  
         </Table>
      </TableContainer>
      </Box>
   );
}); 
